import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ICustomer } from "../../../../../libraries/interfaces/watabilight/IWatabiLightBooking.interface";
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { Yii } from "../../../../../libraries/interfaces/Yii";
import { IFieldSpecs } from "../../../../../libraries/module/formbuilder/fields/interfaces/field-specs";
import { GenericComponent, IAddressValue } from "../generic-component/generic-component.directive";
import { IWatabiLightSectionDetails } from "../../../../../libraries/interfaces/IWenji";
import {
    ISimpleSelectFieldConfig,
    ISimpleSelectOption
} from "../../../../../libraries/module/formbuilder/fields/simple-select/simple-select.interface";
import { faAddressBook, IconDefinition } from "@fortawesome/free-regular-svg-icons";
import { faAt, faHome, faPhone } from "@fortawesome/free-solid-svg-icons";

class CustomerWording {
    public readonly mainTitle: string = Yii.t('wui-shared-js', 'Client');
    public readonly errroFormInvalid: string = Yii.t('wui-shared-js', 'Formulaire invalide, champs obligatoires manquants');
}

interface ICustomerConfig {
    titleConfig: ISimpleSelectFieldConfig;
    firstNameConfig: IFieldSpecs;
    lastNameConfig: IFieldSpecs;
    mailConfig: IFieldSpecs;
    phoneConfig: IFieldSpecs;
    address: {
        zipCodeConfig: IFieldSpecs;
        nameStreetConfig: IFieldSpecs[];
        cityConfig: IFieldSpecs;
        countryConfig: IFieldSpecs;
    };
}

export interface ICustomerValue {
    title: string;
    firstName: string;
    lastName: string;
    mail: string;
    phone: string;
    address: IAddressValue;
}

enum EFormControlName {
    title = "title",
    firstName = "firstName",
    lastName = "lastName",
    mail = "mail",
    phone = "phone",
    address = "address",
    zipCode = "zipCode",
    nameStreet = "nameStreet",
    city = "city",
    country = "country"
}

@Component({
    selector: 'watabilight-customer',
    templateUrl: './customer.component.html',
    styleUrls: [
        './customer.component.scss',
        '../../booking.component.scss'
    ]
})
export class CustomerComponent extends GenericComponent implements OnInit {
    @Input()
    public customer: ICustomer;
    @Input()
    public isEditable: boolean;

    @Output()
    public onEdit: EventEmitter<number> = new EventEmitter();

    public customerWording: CustomerWording = new CustomerWording();
    public isEdit: boolean = false;

    public config: ICustomerConfig;
    public formValue: ICustomerValue;
    public address: string = "";

    public iconMail: IconDefinition = faAt;
    public iconPhone: IconDefinition = faPhone;
    public iconAddress: IconDefinition = faHome;
    public iconAddressBook: IconDefinition = faAddressBook;

    public isInternalError: boolean = true;

    public titleSelection: ISimpleSelectOption[] = [
        {value: "Mr", label: "M"},
        {value: "Mme", label: "Mme"},
    ];
    public withBorder: boolean = true;

    constructor(
        private readonly cdf: ChangeDetectorRef,
    ) {
        super();
    }

    public ngOnInit(): void {
        // TODO POUVOIR SET LE TITRE CLIENT
        this.title = this.sectionConfig?.title ? this.sectionConfig?.title : this.customerWording.mainTitle;
        this.description = this.sectionConfig?.description ? this.sectionConfig?.description : "";
        this.initFormControl();
        this.initFormFieldConfig();
        this.setCustomerValue();
        this.patchValueFromForm();

        this.onEdit.emit(3);
    }

    public update(): void {
        this.cdf.markForCheck();
    }

    public edit(): void {
        if (!this.isEdit) {
            this.isEdit = true;
            this.onEdit.emit(5);
        } else {
            this.form.markAllAsTouched();
            if (this.form.valid && this.checkEmail(this.formValue.mail)) {
                this.onEdit.emit(3);
                this.isEdit = false;
                this.handleError();
            }
        }
    }

    public initFormControl(): void {
        this.form.setControl(EFormControlName.title, new FormControl());
        this.form.setControl(EFormControlName.firstName, new FormControl());
        this.form.setControl(EFormControlName.lastName, new FormControl());
        this.form.setControl(EFormControlName.mail, new FormControl());
        this.form.setControl(EFormControlName.phone, new FormControl());
        this.form.setControl(EFormControlName.address, new FormGroup({
            [EFormControlName.zipCode]: new FormControl(),
            [EFormControlName.nameStreet]: new FormArray([]),
            [EFormControlName.city]: new FormControl(),
            [EFormControlName.country]: new FormControl(),
        }));
    }

    public initFormFieldConfig(): void {
        const addressFormGroup: FormGroup = this.form.get("address") as FormGroup;
        this.config = {
            titleConfig: this.getSimpleSelectConfig(this.form, EFormControlName.title),
            firstNameConfig: this.getFieldConfig(this.form, EFormControlName.firstName),
            lastNameConfig: this.getFieldConfig(this.form, EFormControlName.lastName),
            mailConfig: this.getFieldConfig(this.form, EFormControlName.mail, true),
            phoneConfig: this.getFieldConfig(this.form, EFormControlName.phone),
            address: {
                zipCodeConfig: this.getFieldConfig(addressFormGroup, EFormControlName.zipCode),
                nameStreetConfig: [],
                cityConfig: this.getFieldConfig(addressFormGroup, EFormControlName.city),
                countryConfig: this.getFieldConfig(addressFormGroup, EFormControlName.country)
            }
        };
    }

    public getSimpleSelectConfig(travellerForm: FormGroup, fomControlName: string): ISimpleSelectFieldConfig {
        return {
            formGroup: travellerForm,
            formControlName: fomControlName,
            className: "with-border",
            options: this.titleSelection,
            label: this.wording.type
        };
    }

    public setCustomerValue(): void {
        this.patchValueIfExist(this.find(this.customer, ["title"]), EFormControlName.title);
        this.patchValueIfExist(this.find(this.customer, ["firstName"]), EFormControlName.firstName);
        this.patchValueIfExist(this.find(this.customer, ["lastName"]), EFormControlName.lastName);
        this.patchValueIfExist(this.find(this.customer, ["email"]), EFormControlName.mail);
        this.patchValueIfExist(this.find(this.customer, ["phone"]), EFormControlName.phone);
        this.patchValueIfExist(this.find(this.customer, ["address", "zipCode"]), ["address", EFormControlName.zipCode]);
        this.patchValueIfExist(this.getFirstOrNull(this.find(this.customer, ["address", "city", "nameArray"]), "value"), ["address", EFormControlName.city]);
        this.patchValueIfExist(this.getFirstOrNull(this.find(this.customer, ["address", "country", "nameArray"]), "value"), ["address", EFormControlName.country]);
        this.initAdressForm(this.find(this.customer, ["address", "streetLineArray"]), EFormControlName.nameStreet, this.config);
    }

    public addAddLine(id: number) {
        this.addComplement(this.customer?.address?.streetLineArray, EFormControlName.nameStreet, this.config, id);
    }

    public patchValueFromForm(): void {
        this.formValue = this.form.value;
        this.checkEmail(this.formValue.mail);
        this.address = this.setAddress(this.formValue.address);
    }

    public editCustomerEvent(): void {
        this.patchValueFromForm();
        this.checkEmail(this.formValue.mail);
        this.onEdit.emit(3);
    }

    private find(data: any, key: string[]): any | any[] {
        const editKey: string[] = Object.assign([], key);
        if (editKey.length === 1 && data[editKey[0]]) {
            return (data[editKey[0]]);
        } else if (editKey.length > 1 && data[editKey[0]]) {
            let index: string = editKey[0];
            editKey.shift();
            return this.find(data[index], editKey);
        }
        return null;
    }


}
